<template>
  <div class="agent-board" :class="{ 'agent-board--dark': isDarkSkin }">
    <div v-if="loading" class="agent-board__skeleton">
      <b-skeleton v-for="n in 6" :key="n" height="42px" class="mb-50"/>
    </div>

    <div v-else-if="!rows.length" class="agent-board__empty">
      <i class="fa-solid fa-user-slash"></i>
      <span>{{ $t('salesDashboard.noData') }}</span>
    </div>

    <div v-else class="agent-board__list">
      <button
          v-for="(a, i) in rows"
          :key="a.userId || i"
          type="button"
          class="agent-row"
          @click="$emit('select', { value: a.userId, label: a.name })"
      >
        <span class="agent-row__rank" :class="rankClass(i)">
          <i v-if="i < 3" class="fa-solid fa-medal"></i>
          <template v-else>{{ i + 1 }}</template>
        </span>

        <span class="agent-row__avatar">{{ initials(a.name) }}</span>

        <span class="agent-row__main">
          <span class="agent-row__nameline">
            <span class="agent-row__name">{{ a.name }}</span>
            <span v-if="roleLetter(a)" class="agent-row__role" :class="'is-' + roleLetter(a).toLowerCase()"
                  v-b-tooltip.hover :title="roleTitle(a)">{{ roleLetter(a) }}</span>
          </span>
          <span class="agent-row__bar">
            <span class="agent-row__bar-fill" :style="{ width: barWidth(a) }"></span>
          </span>
          <template v-if="metric !== 'ftd'">
            <span v-if="a.role === 'SALE'" class="agent-row__funnel">
              {{ $t('salesDashboard.leads') }} <strong>{{ a.salesLeads || 0 }}</strong>
              <i class="fa-solid fa-arrow-right-long mx-25"></i>
              {{ $t('salesDashboard.ftd') }} <strong>{{ a.salesFtd || 0 }}</strong>
              <span v-if="a.salesLeads" class="agent-row__conv">({{ convPct(a) }}%)</span>
            </span>
            <span v-else-if="a.role === 'RETENTION'" class="agent-row__funnel">
              {{ $t('salesDashboard.ftd') }} <strong>{{ a.retFtd || 0 }}</strong>
              <i class="fa-solid fa-arrow-right-long mx-25"></i>
              {{ $t('salesDashboard.std') }} <strong>{{ a.retStd || 0 }}</strong>
            </span>
          </template>
        </span>

        <span class="agent-row__metrics">
          <span class="agent-row__deposit" :class="{ 'is-ftd': metric === 'ftd' }">{{ primaryText(a) }}</span>
          <span class="agent-row__sub">{{ subText(a) }}</span>
        </span>

        <i class="fa-solid fa-chevron-right agent-row__chevron"></i>
      </button>
    </div>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'TopAgentsLeaderboard',
  mixins: [mixinBase],
  props: {
    agents: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    isDarkSkin: { type: Boolean, default: false },
    // Show all agents by default (the list scrolls); pass a number to cap it.
    limit: { type: Number, default: 0 },
    // 'deposit' = rank by deposit € (retention/processors); 'ftd' = rank by FTD
    // conversions (sales agents, who never process deposits).
    metric: { type: String, default: 'deposit' },
  },
  computed: {
    rows() {
      // Drop the "no user" bucket (no/unknown agent on the client/deposit).
      // Users who work both sales and retention appear in both tables when they
      // have activity in each — membership is by activity, not by role.
      const list = (this.agents || []).filter(a => a.userId && a.name !== 'No User');
      // limit = 0 → show all (the list scrolls).
      return this.limit > 0 ? list.slice(0, this.limit) : list;
    },
    maxMetric() {
      return this.rows.reduce((m, a) => Math.max(m, this.metricVal(a)), 0) || 1;
    },
    totalMetric() {
      return (this.agents || []).reduce((t, a) => t + this.metricVal(a), 0) || 0;
    },
  },
  methods: {
    metricVal(a) {
      return this.metric === 'ftd' ? (a.salesFtd || 0) : Math.abs(a.deposit || 0);
    },
    primaryText(a) {
      return this.metric === 'ftd' ? (a.salesFtd || 0) : this.convertToEURO(a.deposit);
    },
    subText(a) {
      if (this.metric === 'ftd') {
        // pending leads he currently owns (different cohort from FTDs → no %)
        return `${a.salesLeads || 0} ${this.$t('salesDashboard.leads')}`;
      }
      return `${a.count} ${this.$t('salesDashboard.depositsCount')} · ${this.share(a)}%`;
    },
    barWidth(a) {
      const pct = Math.round((this.metricVal(a) / this.maxMetric) * 100);
      return Math.max(4, pct) + '%';
    },
    share(a) {
      if (!this.totalMetric) return '0';
      return ((a.deposit || 0) / this.totalMetric * 100).toFixed(1);
    },
    rankClass(i) {
      return ['is-gold', 'is-silver', 'is-bronze'][i] || 'is-plain';
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
    },
    roleLetter(a) {
      if (a.role === 'SALE') return 'S';
      if (a.role === 'RETENTION') return 'R';
      return '';
    },
    roleTitle(a) {
      if (a.role === 'SALE') return this.$t('salesDashboard.salesman');
      if (a.role === 'RETENTION') return this.$t('salesDashboard.retention');
      return '';
    },
    convPct(a) {
      if (!a.salesLeads) return '0';
      return ((a.salesFtd || 0) / a.salesLeads * 100).toFixed(0);
    },
  },
};
</script>

<style scoped lang="scss">
.agent-board {
  &__list {
    display: flex; flex-direction: column; gap: 0.35rem;
    max-height: 680px;
    overflow-y: auto;
    scrollbar-width: thin;
    padding-right: 2px;
    &::-webkit-scrollbar { width: 7px; }
    &::-webkit-scrollbar-thumb { background: rgba(0, 123, 255, 0.25); border-radius: 8px; }
    &::-webkit-scrollbar-thumb:hover { background: rgba(0, 123, 255, 0.45); }
  }
  &__empty {
    display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
    padding: 2.5rem 1rem; color: #b9b9c3; font-size: 0.88rem;
    i { font-size: 1.6rem; }
  }
  &__skeleton { padding: 0.25rem 0; }
}

.agent-row {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.55rem 0.65rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;

  &:hover {
    background: rgba(0, 123, 255, 0.06);
    transform: translateX(3px);
    .agent-row__chevron { opacity: 1; transform: translateX(0); }
  }

  &__rank {
    flex-shrink: 0;
    width: 26px; height: 26px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 8px;
    font-size: 0.78rem; font-weight: 700;
    color: #6e6b7b; background: rgba(110, 117, 159, 0.1);
    &.is-gold { color: #fff; background: linear-gradient(135deg, #fbbf24, #f59e0b); }
    &.is-silver { color: #fff; background: linear-gradient(135deg, #cbd5e1, #94a3b8); }
    &.is-bronze { color: #fff; background: linear-gradient(135deg, #d8a07a, #b45309); }
  }

  &__avatar {
    flex-shrink: 0;
    width: 34px; height: 34px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%;
    font-size: 0.74rem; font-weight: 700; color: #fff;
    background: linear-gradient(135deg, #4da3ff, #007BFF);
  }

  &__main { flex: 1; min-width: 0; }
  &__nameline {
    display: flex; align-items: center; gap: 0.4rem;
    margin-bottom: 0.3rem; min-width: 0;
  }
  &__name {
    font-weight: 600; font-size: 0.88rem; color: #1f2a44;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__role {
    flex-shrink: 0;
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 5px;
    font-size: 0.64rem; font-weight: 800; color: #fff; line-height: 1;
    &.is-s { background: linear-gradient(135deg, #34d399, #10b981); }
    &.is-r { background: linear-gradient(135deg, #4da3ff, #007BFF); }
  }
  &__funnel {
    display: block; margin-top: 0.3rem;
    font-size: 0.72rem; color: #6e6b7b; white-space: nowrap;
    strong { color: #1f2a44; font-weight: 700; }
    .agent-row__conv { color: #10b981; font-weight: 700; margin-left: 0.2rem; }
  }
  &__bar {
    display: block; height: 6px; border-radius: 999px;
    background: rgba(110, 117, 159, 0.12); overflow: hidden;
  }
  &__bar-fill {
    display: block; height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #4da3ff, #007BFF);
    transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &__metrics { flex-shrink: 0; text-align: right; }
  &__deposit { display: block; font-weight: 700; font-size: 0.9rem; color: #10b981; }
  &__deposit.is-ftd { color: #007BFF; }
  &__sub { display: block; font-size: 0.72rem; color: #b9b9c3; white-space: nowrap; }

  &__chevron {
    flex-shrink: 0; color: #b9b9c3; font-size: 0.8rem;
    opacity: 0; transform: translateX(-4px);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
}

.agent-board--dark {
  .agent-row {
    &:hover { background: rgba(123, 175, 255, 0.1); }
    &__name { color: #d0d2d6; }
    &__rank.is-plain { color: #8b93ab; background: rgba(255, 255, 255, 0.06); }
    &__bar { background: rgba(255, 255, 255, 0.08); }
    &__sub { color: #7c869c; }
    &__funnel { color: #8b93ab; strong { color: #d0d2d6; } }
  }
}
</style>
